import * as ort from "onnxruntime-web";
import { VehicleType } from "./components/Annotation/VehicleType";

// const CLASSES  = ["car", "convertible", "freight", "pickup", "suv", "unsure", "van"];
const CLASSES  = ["car", "convertible", "pickup", "suv", "van"];
const IMG_SIZE = 32;

export interface ObbLike {
    x:      number;      // normalized center x
    y:      number;      // normalized center y
    width:  number;      // normalized width
    height: number;      // normalized height
    angle:  number;      // degrees
}

interface Rect {
    x:      number;
    y:      number;
    width:  number;
    height: number;
}

let sessionPromise: Promise<ort.InferenceSession> | null = null;

export function parseModelVehicleType ( type: string ) {
    switch( type ) {
        case "car":         { return VehicleType.Car;            }
        case "convertible": { return VehicleType.Convertible;    }
        case "freight":     { return VehicleType.Freight;        }
        case "pickup":      { return VehicleType.Pickup;         }
        case "suv":         { return VehicleType.Suv;            }
        case "unsure":      { return VehicleType.Unsure;         }
        case "van":         { return VehicleType.Van;            }
        default:
            throw Error( "Invalid VehicleType" );
    }
}

export function loadModel(): Promise<ort.InferenceSession> {
    if( !sessionPromise ) {
        ort.env.wasm.wasmPaths = "https://cdn.jsdelivr.net/npm/onnxruntime-web/dist/";
        sessionPromise = ort.InferenceSession.create(
            "/vehicle_type_resnet20_v4.onnx",
            { executionProviders: ["webgpu", "wasm"] }
        );
    }
    return sessionPromise;
}

function softmax( logits: ArrayLike<number> ) {
    let max = logits[0];
    for( let i = 1; i < logits.length; i++ ) {
        if( logits[i] > max ) {
            max = logits[i];
        }
    }
    const exps = Array.from( logits, v => Math.exp( v - max ) );
    const sum  = exps.reduce( ( a, b ) => a + b, 0 );
    return exps.map( x => x / sum );
}

function clamp( v: number, lo: number, hi: number ) {
    return Math.max( lo, Math.min( hi, v ) );
}

// C#-style cast to int for values we expect to be non-negative here.
function intTrunc( v: number ) {
    return v < 0 ? Math.ceil( v ) : Math.floor( v );
}

function getObbBounds( a: ObbLike, imgW: number, imgH: number ): Rect {
    const cx = a.x      * imgW;
    const cy = a.y      * imgH;
    const hw = a.width  * imgW / 2.0;
    const hh = a.height * imgH / 2.0;
    const angleRad = a.angle * Math.PI / 180.0;
    const cos = Math.cos( angleRad );
    const sin = Math.sin( angleRad );

    let minX = Number.POSITIVE_INFINITY;
    let minY = Number.POSITIVE_INFINITY;
    let maxX = Number.NEGATIVE_INFINITY;
    let maxY = Number.NEGATIVE_INFINITY;

    const corners = [
        { x: -hw, y: -hh },
        { x:  hw, y: -hh },
        { x:  hw, y:  hh },
        { x: -hw, y:  hh },
    ];

    for( const pt of corners ) {
        const x = cx + pt.x * cos - pt.y * sin;
        const y = cy + pt.x * sin + pt.y * cos;
        minX = Math.min( minX, x );
        minY = Math.min( minY, y );
        maxX = Math.max( maxX, x );
        maxY = Math.max( maxY, y );
    }
    
    return {
        x:      intTrunc( minX ),
        y:      intTrunc( minY ),
        width:  Math.ceil( maxX - minX ),
        height: Math.ceil( maxY - minY ),
    };
}

function makeSquareRect( rect: Rect, minSize = 32 ): Rect {
    const size    = Math.max( Math.max( rect.width, rect.height ), minSize );
    const centerX = rect.x + intTrunc( rect.width / 2 );
    const centerY = rect.y + intTrunc( rect.height / 2 );
    return {
        x:      centerX - intTrunc( size / 2 ),
        y:      centerY - intTrunc( size / 2 ),
        width:  size,
        height: size,
    };
}

function clampRect( r: Rect, maxW: number, maxH: number ): Rect {
    const x1 = clamp( r.x, 0, maxW );
    const y1 = clamp( r.y, 0, maxH );
    const x2 = clamp( r.x + r.width,  0, maxW );
    const y2 = clamp( r.y + r.height, 0, maxH );
    return {
        x: x1,
        y: y1,
        width: Math.max(  1, x2 - x1 ),
        height: Math.max( 1, y2 - y1 ),
    };
}

function getObbCornersFull( ann: ObbLike, imageWidth: number, imageHeight: number ) {
    const cx = ann.x * imageWidth;
    const cy = ann.y * imageHeight;
    const hw = ann.width  * imageWidth / 2.0;
    const hh = ann.height * imageHeight / 2.0;
    const angleRad = ann.angle * Math.PI / 180.0;
    const cos = Math.cos( angleRad );
    const sin = Math.sin( angleRad );

    return [
        { x: cx + (-hw) * cos - (-hh) * sin, y: cy + (-hw) * sin + (-hh) * cos },
        { x: cx + ( hw) * cos - (-hh) * sin, y: cy + ( hw) * sin + (-hh) * cos },
        { x: cx + ( hw) * cos - ( hh) * sin, y: cy + ( hw) * sin + ( hh) * cos },
        { x: cx + (-hw) * cos - ( hh) * sin, y: cy + (-hw) * sin + ( hh) * cos },
    ];
}

function buildMaskedCrop(
    source:      HTMLCanvasElement,
    ann:         ObbLike,
    imageWidth:  number,
    imageHeight: number
): HTMLCanvasElement {
    const obbBounds = getObbBounds( ann, imageWidth, imageHeight );
    const square    = makeSquareRect( obbBounds, 32 );
    const rect      = clampRect( square, imageWidth, imageHeight );
    const canvas    = document.createElement( "canvas" );
    canvas.width    = rect.width;
    canvas.height   = rect.height;
    const ctx       = canvas.getContext( "2d" )!;
    ctx.clearRect( 0, 0, canvas.width, canvas.height );
    ctx.drawImage(
        source,
        rect.x, rect.y, rect.width, rect.height,
             0,      0, rect.width, rect.height
    );

    const cornersFull  = getObbCornersFull( ann, imageWidth, imageHeight );
    const cornersLocal = cornersFull.map( p => ({ x: p.x - rect.x, y: p.y - rect.y }) );

    //fill the entire dst rect black
    ctx.fillStyle = "black";
    ctx.fillRect( 0, 0, rect.width, rect.height );

    //draw the obb clip path
    ctx.beginPath();
    ctx.moveTo( cornersLocal[0].x, cornersLocal[0].y );
    for( let i = 1; i < cornersLocal.length; i++ ) {
        ctx.lineTo( cornersLocal[i].x, cornersLocal[i].y );
    }
    ctx.closePath();

    //draw only where the clip shape lets us
    ctx.save();
    ctx.clip();
    ctx.drawImage( 
        source,
        rect.x, rect.y, rect.width, rect.height, 
             0,      0, rect.width, rect.height
    );
    ctx.restore();
    return canvas;
}

// Mirrors dataset preprocessing intent:
// LongestMaxSize(32) -> centered black pad -> /255.0
function preprocessToModelInput( maskedCrop: HTMLCanvasElement ): Float32Array {
    const srcW = maskedCrop.width;
    const srcH = maskedCrop.height;

    const scale = Math.min( IMG_SIZE / srcW, IMG_SIZE / srcH );
    const nw = Math.max( 1, Math.round( srcW * scale ) );
    const nh = Math.max( 1, Math.round( srcH * scale ) );
    const dx = Math.floor( ( IMG_SIZE - nw ) / 2 );
    const dy = Math.floor( ( IMG_SIZE - nh ) / 2 );

    const canvas = document.createElement( "canvas" );
    canvas.width = IMG_SIZE;
    canvas.height = IMG_SIZE;

    const ctx = canvas.getContext( "2d" )!;
    ctx.clearRect( 0, 0, IMG_SIZE, IMG_SIZE );
    ctx.drawImage( maskedCrop, 0, 0, srcW, srcH, dx, dy, nw, nh );

    const { data } = ctx.getImageData( 0, 0, IMG_SIZE, IMG_SIZE );
    const out = new Float32Array( 3 * IMG_SIZE * IMG_SIZE );
    const n   = IMG_SIZE * IMG_SIZE;
    for( let i = 0; i < n; i++ ) {
        out[0 * n + i] = data[i * 4 + 0] / 255.0;
        out[1 * n + i] = data[i * 4 + 1] / 255.0;
        out[2 * n + i] = data[i * 4 + 2] / 255.0;
    }
    return out;
}

interface LabelProbability {
    label:      string;
    confidence: number
}

export interface ClassifyOutput {
    label:            string; 
    confidence:       number;
    probs:            LabelProbability[];
    maskedCropCanvas: HTMLCanvasElement;
}

interface Image {
    width:       number;
    height:      number;
    pixelWidth:  number;
    pixelHeight: number;
}

export async function classifyFromCanvas(
    model:        ort.InferenceSession,
    sourceCanvas: HTMLCanvasElement,
    ann:          ObbLike,
    imageWidth:   number,
    imageHeight:  number,
    pixelWidth:   number,
    pixelHeight:  number,
): Promise<ClassifyOutput> {
    const maskedCropCanvas = buildMaskedCrop( sourceCanvas, ann, imageWidth, imageHeight );
    const input            = preprocessToModelInput( maskedCropCanvas );
    const tensor           = new ort.Tensor( "float32", input, [1, 3, IMG_SIZE, IMG_SIZE] );
    const results          = await model.run( { pixel_values: tensor } );
    const logits           = results.logits.data as Float32Array;
    const probs            = softmax( logits );
    let maxIdx = 0;
    for( let i = 1; i < probs.length; i++ ) {
        if( probs[i] > probs[maxIdx] ) {
            maxIdx = i;
        }
    }
    return {
        label: CLASSES[maxIdx],
        confidence: probs[maxIdx],
        probs: CLASSES
            .map( ( label, i ) => ({ label, confidence: probs[i] }) )
            .sort( ( a, b ) => b.confidence - a.confidence ),
        maskedCropCanvas
    };
}